import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { StatusBadge } from "../../../ui/components/StatusBadge";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { useI18n } from "../../lib/i18n";
import {
  fetchSupportRequest,
  markSupportRequestHandled,
  reopenSupportRequest,
  type SupportRequestDetail,
} from "../../lib/api/insights";
import { InsightDetailsView, InsightSources } from "../products/insights/InsightCard";
import { IMPACT_BADGE } from "./SupportInboxPage";
import * as s from "./SupportRequestDetailModal.css";

export function SupportRequestDetailModal({
  idOrReference,
  token,
  onClose,
  onChanged,
}: {
  idOrReference: string;
  token: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [request, setRequest] = React.useState<SupportRequestDetail | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setRequest(null);
    setLoadError(null);
    fetchSupportRequest(token, idOrReference)
      .then((r) => {
        if (!cancelled) setRequest(r);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : t("pro.system.insights.support.loadFailed"));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, idOrReference]);

  const update = async (action: "handle" | "reopen") => {
    if (!token || !request || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const next =
        action === "handle"
          ? await markSupportRequestHandled(token, request.id, note.trim() || null)
          : await reopenSupportRequest(token, request.id);
      setRequest(next);
      setNote("");
      onChanged();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t("pro.system.insights.support.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handled = Boolean(request?.handledAt);
  const subject =
    request?.insightTitle ??
    (request?.alertCode ? t("pro.system.insights.support.alertOnly", { code: request.alertCode }) : "");

  return (
    <Modal
      open
      setOpen={(open) => {
        if (!open && !saving) onClose();
      }}
      closeOnClickOff={!saving}
      minWidth="480px"
      minHeight="auto"
      maxWidth="720px"
    >
      <Modal.Header>
        <h3>
          {request
            ? t("pro.system.insights.support.detailTitle", { reference: request.reference })
            : t("pro.system.insights.support.title")}
        </h3>
      </Modal.Header>
      <Modal.Body>
        {loadError ? (
          <Callout variant="error">{loadError}</Callout>
        ) : !request ? (
          <AuthTransitionScreen variant="compact" message={t("pro.system.insights.loading")} />
        ) : (
          <div className={s.body}>
            <dl className={s.meta}>
              <dt className={s.metaTerm}>{t("pro.system.insights.support.col.status")}</dt>
              <dd className={s.metaValue}>
                {handled ? (
                  <StatusBadge size="sm" variant="enrolled" label={t("pro.system.insights.support.status.handled")} />
                ) : (
                  <StatusBadge size="sm" variant="pending" label={t("pro.system.insights.support.status.open")} />
                )}
              </dd>
              <dt className={s.metaTerm}>{t("pro.system.insights.support.col.impact")}</dt>
              <dd className={s.metaValue}>
                {request.impact ? (
                  <StatusBadge
                    size="sm"
                    variant={IMPACT_BADGE[request.impact]}
                    label={t(`pro.system.insights.support.impact.${request.impact}`)}
                  />
                ) : (
                  "—"
                )}
              </dd>
              <dt className={s.metaTerm}>{t("pro.system.insights.support.col.received")}</dt>
              <dd className={s.metaValue}>{new Date(request.createdAt).toLocaleString()}</dd>
              <dt className={s.metaTerm}>{t("pro.system.insights.support.col.requester")}</dt>
              <dd className={s.metaValue}>
                {request.requestedByName ?? "—"}
                {request.requestedByEmail ? (
                  <>
                    {" · "}
                    <a className={s.link} href={`mailto:${request.requestedByEmail}?subject=${encodeURIComponent(request.reference)}`}>
                      {request.requestedByEmail}
                    </a>
                  </>
                ) : null}
              </dd>
              <dt className={s.metaTerm}>{t("pro.system.insights.support.callback")}</dt>
              <dd className={s.metaValue}>
                {request.callbackPhone ? (
                  <a className={s.link} href={`tel:${request.callbackPhone.replace(/[^\d+]/g, "")}`}>
                    {request.callbackPhone}
                  </a>
                ) : (
                  t("pro.system.insights.support.callbackNone")
                )}
              </dd>
              <dt className={s.metaTerm}>{t("pro.system.insights.support.col.product")}</dt>
              <dd className={s.metaValue}>
                <button
                  type="button"
                  className={s.link}
                  onClick={() => navigate({ to: "/workspace/products/$productId", params: { productId: request.productId } })}
                >
                  {request.productName ?? request.productId}
                </button>
              </dd>
            </dl>

            <section className={s.section}>
              <h4 className={s.sectionHeading}>{t("pro.system.insights.support.triedHeading")}</h4>
              {request.stepsTried.length ? (
                <ul className={s.list}>
                  {request.stepsTried.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ul>
              ) : (
                <p className={s.muted}>{t("pro.system.insights.support.none")}</p>
              )}
            </section>

            <section className={s.section}>
              <h4 className={s.sectionHeading}>{t("pro.system.insights.support.notesHeading")}</h4>
              {request.notes ? <p className={s.text}>{request.notes}</p> : <p className={s.muted}>{t("pro.system.insights.support.none")}</p>}
            </section>

            <section className={s.section}>
              <h4 className={s.sectionHeading}>{t("pro.system.insights.support.insightHeading")}</h4>
              <div className={s.insightBox}>
                <strong>
                  {subject}
                  {request.insightTitle && request.alertCode ? ` · ${request.alertCode}` : ""}
                </strong>
                {request.insight ? (
                  <>
                    <p className={s.text}>{request.insight.summary}</p>
                    {request.insight.details ? (
                      <>
                        <InsightDetailsView details={request.insight.details} />
                        <InsightSources details={request.insight.details} />
                      </>
                    ) : (
                      <p className={s.muted}>{t("pro.system.insights.support.noAnalysis")}</p>
                    )}
                  </>
                ) : (
                  <p className={s.muted}>{t("pro.system.insights.support.noInsight")}</p>
                )}
              </div>
            </section>

            {handled ? (
              <section className={s.section}>
                <h4 className={s.sectionHeading}>{t("pro.system.insights.support.handledHeading")}</h4>
                <p className={s.text}>
                  {t("pro.system.insights.support.handledBy", {
                    name: request.handledByUserName ?? "—",
                    when: new Date(request.handledAt!).toLocaleString(),
                  })}
                </p>
                {request.handledNote ? <p className={s.text}>{request.handledNote}</p> : null}
              </section>
            ) : (
              <div>
                <label className={s.label} htmlFor="support-handled-note">
                  {t("pro.system.insights.support.handledNote")}
                </label>
                <textarea
                  id="support-handled-note"
                  className={s.textarea}
                  value={note}
                  maxLength={2000}
                  disabled={saving}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("pro.system.insights.support.handledNotePlaceholder")}
                />
              </div>
            )}

            {saveError ? <Callout variant="error">{saveError}</Callout> : null}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" color="neutral" variant="outline" size="md" disabled={saving} onClick={onClose}>
          {t("pro.system.insights.support.close")}
        </Button>
        {request ? (
          handled ? (
            <Button type="button" color="neutral" variant="outline" size="md" loading={saving} onClick={() => void update("reopen")}>
              {t("pro.system.insights.support.reopen")}
            </Button>
          ) : (
            <Button type="button" color="primary" variant="solid" size="md" loading={saving} onClick={() => void update("handle")}>
              {t("pro.system.insights.support.markHandled")}
            </Button>
          )
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
